import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, getUser, logout } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Building2, Users, MessageSquareText, LogOut, Plus, BarChart3, Copy, Trash2, X, Search, Inbox, Menu } from "lucide-react";

/* =============== Shared layout =============== */
export function DashboardShell({ title, subtitle, nav, children, testId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  function doLogout() {
    logout();
    navigate("/login");
  }

  return (
    <div className="min-h-screen bg-white flex" data-testid={testId}>
      {/* Sidebar (desktop) + drawer (mobile) */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setMobileOpen(false)} />
      )}
      <aside className={`
        w-60 border-r border-gray-200 bg-gray-50 min-h-screen flex flex-col shrink-0
        md:relative md:translate-x-0
        fixed inset-y-0 left-0 z-50 transition-transform
        ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
      `}>
        <div className="px-5 pt-5 pb-6 border-b border-gray-200 flex items-center justify-between">
          <Logo className="h-7" />
          <button className="md:hidden text-gray-500" onClick={() => setMobileOpen(false)}><X size={18} /></button>
        </div>
        <div className="px-3 pt-4 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold px-3 mb-2">{title}</div>
          <nav className="space-y-0.5">
            {nav.map((n) => {
              const active = location.pathname === n.to || (n.match && location.pathname.startsWith(n.match));
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm ${active ? "bg-white text-gray-900 border border-gray-200" : "text-gray-600 hover:text-gray-900 hover:bg-white"}`}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.icon}
                  {n.label}
                </Link>
              );
            })}
          </nav>
        </div>
        <div className="p-3 border-t border-gray-200">
          <div className="text-xs text-gray-500 mb-1 truncate">{user?.email}</div>
          <button onClick={doLogout} className="text-xs text-gray-600 hover:text-gray-900 flex items-center gap-1.5" data-testid="logout-btn">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="h-16 border-b border-gray-200 flex items-center px-4 md:px-8 justify-between sticky top-0 bg-white z-30">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-gray-600" onClick={() => setMobileOpen(true)} data-testid="mobile-sidebar-open">
              <Menu size={20} />
            </button>
            <div className="font-display text-lg md:text-xl font-semibold text-gray-900">{subtitle || title}</div>
          </div>
        </header>
        <main className="p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}

export function StatCard({ label, value, testId }) {
  return (
    <div className="border border-gray-200 rounded-xl p-5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold">{label}</div>
      <div className="font-display text-3xl font-semibold tracking-tight text-gray-900 mt-3">{value ?? "—"}</div>
    </div>
  );
}

export function SectionTitle({ title, action }) {
  return (
    <div className="flex items-center justify-between mb-4 mt-2">
      <h2 className="font-display text-lg font-semibold tracking-tight text-gray-900">{title}</h2>
      {action}
    </div>
  );
}

export function Btn({ children, onClick, variant = "primary", type = "button", testId, disabled, className = "" }) {
  const styles = {
    primary: "bg-gray-900 text-white hover:bg-gray-800",
    secondary: "bg-white text-gray-900 border border-gray-200 hover:bg-gray-50",
    danger: "bg-white text-red-600 border border-red-200 hover:bg-red-50",
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${styles[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function Input({ label, value, onChange, type = "text", required, placeholder, testId, rows }) {
  const isTextarea = !!rows;
  const Comp = isTextarea ? "textarea" : "input";
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">{label}</label>
      <Comp
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        rows={rows}
        data-testid={testId}
        className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
      />
    </div>
  );
}

export function Modal({ open, onClose, title, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="font-display text-lg font-semibold">{title}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="modal-close"><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function EmptyState({ icon, title, body, action }) {
  return (
    <div className="border border-dashed border-gray-200 rounded-xl py-16 text-center">
      <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center mx-auto">{icon || <Inbox size={18} />}</div>
      <div className="font-display text-lg font-semibold text-gray-900 mt-4">{title}</div>
      <div className="text-sm text-gray-500 mt-1">{body}</div>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
